import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {FormFieldComponent} from '../../../../shared/components/form-field/form-field.component';
import {DisparoAutomaticoModel} from '../../../../shared/model/disparo-automatico/disparo-automatico.model';
import {ButtonPadraoComponent} from '../../../../shared/components/button-padrao/button-padrao.component';
import {DatePipe, TitleCasePipe} from '@angular/common';
import {MsgService} from '../../../../shared/service/msg.service';
import {DisparoAutomaticoService} from '../../../../shared/service/disparo-automatico.service';
import {MatDialog} from '@angular/material/dialog';
import {ModalConfirmacaoComponent} from '../../../../shared/modais/modal-confirmacao/modal-confirmacao.component';
import {LogRotina} from '../../../../shared/model/log-rotina.model';
import {environment} from '../../../../../../environments/environment';
import {DomSanitizer} from '@angular/platform-browser';
import {AuthService} from '../../../../shared/service/auth.service';
import {ProgramacaoDisparoModel} from '../../../../shared/model/disparo-automatico/programacao-disparo.model';
import {
  ProgramacaoDisparoFormComponent,
  TipoProgramacaoDisparo
} from '../programacao-disparo-form/programacao-disparo-form.component';
import {classeStatusDisparo} from '../../../../shared/model/disparo-automatico/status-disparo.util';
import {
  COR_BOTAO_PAUSAR_DISPARO,
  MENSAGEM_DISPARO_DESPAUSADO,
  MENSAGEM_DISPARO_PAUSADO,
  STATUS_DISPARO_PAUSADO,
  STATUS_DISPARO_PAUSAVEL,
  TEXTO_CONFIRMACAO_PAUSAR_DISPARO,
  TIPO_DISPARO_PAUSAVEL
} from './utils/disparo-info.constantes';

@Component({
  selector: 'app-disparo-info',
  standalone: true,
  imports: [
    FormFieldComponent,
    ProgramacaoDisparoFormComponent,
    ButtonPadraoComponent,
    DatePipe,
    TitleCasePipe
  ],
  templateUrl: './disparo-info.component.html',
  styleUrl: './disparo-info.component.css'
})
export class DisparoInfoComponent implements OnInit {
  @Output() envioDisparoAutomatico = new EventEmitter<DisparoAutomaticoModel>();
  @Output() salvar = new EventEmitter<boolean>();
  @Output() enviar = new EventEmitter<boolean>();
  @Output() cancelar = new EventEmitter<boolean>();
  @Output() tipoProgramacaoChange = new EventEmitter<TipoProgramacaoDisparo>();
  @Output() programacaoChange = new EventEmitter<ProgramacaoDisparoModel>();

  @Input() disparoAutomatico: DisparoAutomaticoModel = new DisparoAutomaticoModel();
  @Input() exibirProgramacao = false;
  @Input() tipoProgramacao: TipoProgramacaoDisparo = 'Padrão';
  @Input() programacao: ProgramacaoDisparoModel = new ProgramacaoDisparoModel();
  @Input() salvando = false;
  @Input() cadastroInvalido = false;
  @Input() motivoCadastroInvalido = '';

  readonly classeStatusDisparo = classeStatusDisparo;

  readonly corBotaoPausar = COR_BOTAO_PAUSAR_DISPARO;

  interacoesUsuarios: LogRotina[] = [];
  alterandoPausa = false;

  private readonly disparoAutomaticoService = inject(DisparoAutomaticoService);
  private readonly msgService = inject(MsgService);
  private readonly dialog = inject(MatDialog);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);

  ngOnInit() {
    this.buscarLogsDisparos();
  }

  get podeEditar(): boolean {
    if (this.disparoAutomatico.ehProgramacaoRecorrente) {
      return (this.programacao.enviosRealizados ?? this.disparoAutomatico.quantidadeEnvios ?? 0) < 1;
    }
    const status = this.disparoAutomatico.status;
    return !status || status === 'Agendado' || status === 'Aguardando Envio';
  }

  get podeSalvar(): boolean {
    if (this.disparoAutomatico.ehProgramacaoRecorrente) {
      return this.programacao.ativo !== false;
    }
    return this.podeEditar;
  }

  get envioBloqueado(): boolean {
    if (this.disparoAutomatico.ehProgramacaoRecorrente) return !this.podeEditar;
    const status = this.disparoAutomatico.status;
    return !(!status || status === 'Agendado' || status === 'Aguardando Envio');
  }

  get enviarDesabilitado(): boolean {
    return this.salvando
      || this.envioBloqueado
      || !(!this.disparoAutomatico.dataAgendamento && !this.disparoAutomatico.horaAgendamento);
  }

  alterarTitulo(titulo: string) {
    this.disparoAutomatico.tituloDisparo = titulo;
    this.envioDisparoAutomatico.emit(this.disparoAutomatico);
  }

  receberAgendamento(event: any) {
    const rawData = event.data;
    let data: Date | null = null;

    if (rawData instanceof Date) {
      data = rawData;
    } else if (typeof rawData === 'string') {
      data = new Date(rawData);
    } else if (rawData?.toDate) {
      data = rawData.toDate();
    }

    this.disparoAutomatico.dataAgendamento = data!;
    this.disparoAutomatico.horaAgendamento = event.hora;
    this.envioDisparoAutomatico.emit(this.disparoAutomatico);
  }

  atualizarTipoProgramacao(tipo: TipoProgramacaoDisparo): void {
    this.tipoProgramacao = tipo;
    if (tipo === 'Recorrente') {
      this.disparoAutomatico.dataAgendamento = undefined;
      this.disparoAutomatico.horaAgendamento = undefined;
      this.envioDisparoAutomatico.emit(this.disparoAutomatico);
    }
    this.tipoProgramacaoChange.emit(tipo);
  }

  atualizarProgramacao(programacao: ProgramacaoDisparoModel): void {
    this.programacao = programacao;
    this.programacaoChange.emit(programacao);
  }

  salvarDisparoAutomatico() {
    this.salvar.emit(true);
  }

  enviarDisparoAutomatico() {
    this.enviar.emit(true);
  }

  cancelarEdicao() {
    this.cancelar.emit(true);
  }

  get podePausar(): boolean {
    return STATUS_DISPARO_PAUSAVEL.includes(this.disparoAutomatico.status ?? '');
  }

  get estaPausado(): boolean {
    return this.disparoAutomatico.status === STATUS_DISPARO_PAUSADO;
  }

  pausarDisparo() {
    this.dialog.open(ModalConfirmacaoComponent, {
      data: { texto: TEXTO_CONFIRMACAO_PAUSAR_DISPARO }
    }).afterClosed().subscribe({
      next: res => {
        if (!res) return;
        this.alterandoPausa = true;
        this.disparoAutomaticoService.pausarDisparo(this.disparoAutomatico.id!).subscribe({
          next: status => this.aplicarStatusPausa(status, MENSAGEM_DISPARO_PAUSADO),
          error: () => this.alterandoPausa = false
        });
      }
    });
  }

  despausarDisparo() {
    this.alterandoPausa = true;
    this.disparoAutomaticoService.despausarDisparo(this.disparoAutomatico.id!).subscribe({
      next: status => this.aplicarStatusPausa(status, MENSAGEM_DISPARO_DESPAUSADO),
      error: () => this.alterandoPausa = false
    });
  }

  private aplicarStatusPausa(status: string, mensagem: string) {
    this.alterandoPausa = false;
    this.disparoAutomatico.status = status;
    this.envioDisparoAutomatico.emit(this.disparoAutomatico);
    this.msgService.msgSucesso(mensagem);
    this.buscarLogsDisparos();
  }

  cancelarEnvios(element: any) {
    this.dialog.open(ModalConfirmacaoComponent, {
      data: { texto: 'Deseja cancelar os envios dos disparos?' }
    }).afterClosed().subscribe({
      next: res => {
        if (res) {
          this.disparoAutomaticoService.alterarStatusDisparoAutomatico('Cancelado', element.id).subscribe({
            next: () => {
              this.msgService.msgSucesso('Disparo Automático cancelado os envios!');
              this.cancelarEdicao();
            }
          });
        }
      }
    });
  }

  buscarLogsDisparos() {
    if (this.disparoAutomatico.id) {
      this.disparoAutomaticoService.buscarLogsDisparosAutomaticos(this.disparoAutomatico.id!).subscribe({
        next: res => this.interacoesUsuarios = res
      });
    }
  }

  gerarUrl(nomeArquivo: string, extensao: string, path: string) {
    return this.domSanitizer.bypassSecurityTrustUrl(`${environment.urlRepositorioClaribot}/${this.authService.getUsuarioLogado().grupo}/${path}/${nomeArquivo}.${extensao}`);
  }
}
